import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Button,
  Menu,
  MenuButton,
  Avatar,
  MenuList,
  MenuItem,
  MenuDivider,
  Image,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  useDisclosure,
  HStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, HamburgerIcon } from "@chakra-ui/icons";
import { Link, useNavigate } from "react-router-dom";
import { ChatState } from "../../Context/ChatProvider";
import logo from "../Pictures/logo.png";

const Navigation = () => {
  const { user, setUser } = ChatState();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentLang, setCurrentLang] = useState("en");

  useEffect(() => {
    // Add Google Translate script
    const addScript = document.createElement("script");
    addScript.src =
      "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    addScript.async = true;

    // Initialize function
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: "en,ms",
        },
        "google_translate_element"
      );
    };

    document.body.appendChild(addScript);

    return () => {
      delete window.googleTranslateElementInit;
      const scripts = document.querySelectorAll(
        'script[src*="translate.google.com"]'
      );
      scripts.forEach((script) => script.remove());
    };
  }, []);

  const changeLanguage = (lang) => {
    // Prevent duplicate calls
    if (currentLang === lang) return;

    // Find and trigger the Google Translate select element
    const select = document.querySelector(".goog-te-combo");
    if (select) {
      select.value = lang;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      setCurrentLang(lang);
    } else {
      // If not immediately available, wait briefly for it to load
      setTimeout(() => {
        const selectDelayed = document.querySelector(".goog-te-combo");
        if (selectDelayed) {
          selectDelayed.value = lang;
          selectDelayed.dispatchEvent(new Event("change", { bubbles: true }));
          setCurrentLang(lang);
        }
      }, 500);
    }
  };

  const logoutHandler = () => {
    localStorage.removeItem("authToken");
    setUser(null); // Clear context
    navigate("/");
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      bg="white"
      w="100%"
      p={2}
      top="0"
      zIndex="1000"
      boxShadow="md"
    >
      {/* Hamburger Menu for Mobile */}
      <IconButton
        icon={<HamburgerIcon />}
        aria-label="Open Menu"
        display={{ base: "flex", md: "none" }}
        onClick={onOpen}
        variant="ghost"
        fontSize="24px"
        ml={2}
      />

      {/* Logo Section */}
      <Image
        src={logo}
        alt="Logo"
        width={{ base: "40px", md: "57px" }}
        marginLeft={{ base: "10px", md: "20px" }}
        objectFit="contain"
        alignSelf="center"
        marginY={0}
      />

      {/* Navigation Links Section - Hidden on Mobile */}
      <Box
        margin={{ base: "0 30px", md: "0 300px" }}
        display={{ base: "none", md: "flex" }}
        justifyContent="space-between"
        w="100%"
      >
        <Link to="/">
          <Text fontSize={{ base: "md", md: "lg" }}>Home</Text>
        </Link>
        <Link to="/chats">
          <Text fontSize={{ base: "md", md: "lg" }}>Chats</Text>
        </Link>
        <Link to="/our-promise">
          <Text fontSize={{ base: "md", md: "lg" }}>Promise</Text>
        </Link>
        <Link to="/tricks-of-the-trade">
          <Text fontSize={{ base: "md", md: "lg" }}>Advice</Text>
        </Link>
        <Link to="/support">
          <Text fontSize={{ base: "md", md: "lg" }}>Support</Text>
        </Link>
      </Box>

      {/* Mobile Drawer Menu */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Menu</DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch" mt={4}>
              <Link to="/" onClick={onClose}>
                <Text
                  fontSize="lg"
                  p={2}
                  _hover={{ bg: "gray.100", borderRadius: "md" }}
                >
                  Home
                </Text>
              </Link>
              <Link to="/chats" onClick={onClose}>
                <Text
                  fontSize="lg"
                  p={2}
                  _hover={{ bg: "gray.100", borderRadius: "md" }}
                >
                  Chats
                </Text>
              </Link>
              <Link to="/our-promise" onClick={onClose}>
                <Text
                  fontSize="lg"
                  p={2}
                  _hover={{ bg: "gray.100", borderRadius: "md" }}
                >
                  Promise
                </Text>
              </Link>
              <Link to="/tricks-of-the-trade" onClick={onClose}>
                <Text
                  fontSize="lg"
                  p={2}
                  _hover={{ bg: "gray.100", borderRadius: "md" }}
                >
                  Advice
                </Text>
              </Link>
              <Link to="/support" onClick={onClose}>
                <Text
                  fontSize="lg"
                  p={2}
                  _hover={{ bg: "gray.100", borderRadius: "md" }}
                >
                  Support
                </Text>
              </Link>

              {/* Language Toggle Buttons for Mobile */}
              <Box pt={4} borderTopWidth="1px">
                <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.600">
                  Language
                </Text>
                <HStack spacing={2} className="notranslate">
                  <Button
                    flex={1}
                    size="md"
                    variant={currentLang === "en" ? "solid" : "outline"}
                    colorScheme={currentLang === "en" ? "orange" : "gray"}
                    onClick={() => {
                      changeLanguage("en");
                      onClose();
                    }}
                    fontWeight="600"
                    className="notranslate"
                  >
                    ENG
                  </Button>
                  <Button
                    flex={1}
                    size="md"
                    variant={currentLang === "ms" ? "solid" : "outline"}
                    colorScheme={currentLang === "ms" ? "orange" : "gray"}
                    onClick={() => {
                      changeLanguage("ms");
                      onClose();
                    }}
                    fontWeight="600"
                    className="notranslate"
                  >
                    BM
                  </Button>
                </HStack>
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Google Translate Widget - Hidden but functional */}
      <Box
        id="google_translate_element"
        position="absolute"
        left="-9999px"
        visibility="hidden"
        pointerEvents="none"
      />

      {/* Language Toggle Buttons */}
      <HStack
        spacing={2}
        mr={4}
        display={{ base: "none", md: "flex" }}
        className="notranslate"
      >
        <Button
          size="sm"
          variant={currentLang === "en" ? "solid" : "outline"}
          colorScheme={currentLang === "en" ? "orange" : "gray"}
          onClick={() => changeLanguage("en")}
          fontWeight="600"
          className="notranslate"
        >
          ENG
        </Button>
        <Button
          size="sm"
          variant={currentLang === "ms" ? "solid" : "outline"}
          colorScheme={currentLang === "ms" ? "orange" : "gray"}
          onClick={() => changeLanguage("ms")}
          fontWeight="600"
          className="notranslate"
        >
          BM
        </Button>
      </HStack>

      {/* User Authentication Section */}
      {user ? (
        <Menu>
          {/* User Avatar and Dropdown Menu */}
          <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
            <Avatar
              size="sm"
              cursor="pointer"
              name={user ? user.name : "Guest"}
              src={user ? user.pic : ""}
            />
          </MenuButton>

          <MenuList>
            <MenuItem onClick={() => navigate(`/profile`)}>My Profile</MenuItem>
            {user.role === "vendor" && (
              <>
                <MenuItem onClick={() => navigate("/vendor-dashboard")}>
                  Dashboard
                </MenuItem>
                <MenuItem onClick={() => navigate("/vendor-jobs")}>
                  Jobs
                </MenuItem>
              </>
            )}
            {user.role === "admin" && (
              <>
                <MenuDivider />
                <MenuItem onClick={() => navigate("/admin")}>
                  Admin Dashboard
                </MenuItem>
              </>
            )}
            <MenuDivider />
            <MenuItem onClick={logoutHandler}>Logout</MenuItem>
          </MenuList>
        </Menu>
      ) : (
        // Show Login/Signup Button when no user is logged in
        <Button
          colorScheme="orange" // Orange button color
          onClick={() => navigate("/login")} // Navigate to login/signup page
          mr={{ base: "10px", md: "20px" }} // Add right margin to button
        >
          Login
        </Button>
      )}
    </Box>
  );
};

export default Navigation;
